import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { type ArticleSuggestionsResponse } from './types';
import { zodArticleSuggestionsResponseSchema } from './validations';

type ArticleSuggestionsParams = {
  limit?: number;
  query?: string;
  siteId?: UUID | null;
  supplierId: UUID;
};

/**
 * Fetches the top articles a supplier has previously delivered to a site,
 * ranked by the number of distinct delivery notes.
 *
 * @param params - Required site and supplier IDs, optional result limit (1–50, default 5).
 * @param options - Additional request options (e.g. abort signal).
 * @returns The article suggestions response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_article_suggestions_delivery_note_suggestions_articles_get
 */
export const fetchArticleSuggestions = async (
  { limit, query, siteId, supplierId }: ArticleSuggestionsParams,
  options?: KyRequestOptions,
): Promise<ArticleSuggestionsResponse> => {
  const searchParams = {
    ...(limit !== undefined && { limit }),
    ...(query?.trim() && { query: query.trim() }),
    ...(siteId != null && { site_id: siteId }),
    supplier_id: supplierId,
  };

  const response = await vestigasApi
    .get(ENDPOINT.DELIVERY_NOTE.GET_ARTICLE_SUGGESTIONS(), {
      searchParams,
      ...options,
    })
    .json<ArticleSuggestionsResponse>();

  const validated = zodArticleSuggestionsResponseSchema.safeParse(response);

  if (validated.error?.issues?.length) {
    log.error({
      message: 'Article suggestions API response has validation errors',
      error: {
        errors: validated.error.issues,
        errorMessage: validated.error.message,
        response,
      },
    });
  }

  return response;
};
